/**
 * AICommandContext - Contexto global para comandos del AI Assistant
 * Permite que el AIMiniChat emita comandos que cualquier página puede escuchar
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { AILeadCommand } from '@/types/ai/commands.types';

interface AICommandContextValue {
  /** Comando pendiente de ejecutar */
  pendingCommand: AILeadCommand | null;

  /** Emitir un nuevo comando */
  emitCommand: (command: AILeadCommand) => void;

  /** Marcar comando como ejecutado */
  clearCommand: () => void;
}

const AICommandContext = createContext<AICommandContextValue | undefined>(undefined);

interface AICommandProviderProps {
  children: ReactNode;
}

export const AICommandProvider: React.FC<AICommandProviderProps> = ({ children }) => {
  const [pendingCommand, setPendingCommand] = useState<AILeadCommand | null>(null);

  const emitCommand = useCallback((command: AILeadCommand) => {
    setPendingCommand(command);
  }, []);

  const clearCommand = useCallback(() => {
    setPendingCommand(null);
  }, []);

  return (
    <AICommandContext.Provider
      value={{
        pendingCommand,
        emitCommand,
        clearCommand,
      }}
    >
      {children}
    </AICommandContext.Provider>
  );
};

export const useAICommand = () => {
  const context = useContext(AICommandContext);
  if (context === undefined) {
    throw new Error('useAICommand debe usarse dentro de un AICommandProvider');
  }
  return context;
};
